import React, { useEffect, useMemo, useRef, useState } from "react";
import ControlRoomSvg from "./control-room.svg";

// Union type for label class names (nodes with id="label-xxx")
export type ControlRoomLabelItem =
  | "label-interface"
  | "label-speakers"
  | "label-headphones"
  | "label-monitoring-controller"
  | "label-studio-room";

// Union type for interactive element class names (nodes with corresponding functionality)
export type ControlRoomInteractiveItem =
  | "element-studio-room"
  | "element-studio-room-active"
  | "element-speakers-2"
  | "element-speakers-2-active"
  | "element-interface"
  | "element-interface-active"
  | "element-speakers-1"
  | "element-speakers-1-active"
  | "element-headphones"
  | "element-headphones-active"
  | "element-monitoring-controller"
  | "element-monitoring-controller-active";

// Mapping between labels and their corresponding interactive items
// Some labels can control multiple elements (like speakers controlling both speakers-1 and speakers-2)
const labelToInteractiveMap: Record<
  ControlRoomLabelItem,
  { normal: ControlRoomInteractiveItem[]; active: ControlRoomInteractiveItem[] }
> = {
  "label-studio-room": { normal: ["element-studio-room"], active: ["element-studio-room-active"] },
  "label-speakers": {
    normal: ["element-speakers-2"],
    active: ["element-speakers-1-active", "element-speakers-2-active"],
  },
  "label-interface": { normal: ["element-interface"], active: ["element-interface-active"] },
  "label-headphones": { normal: ["element-headphones"], active: ["element-headphones-active"] },
  "label-monitoring-controller": {
    normal: ["element-monitoring-controller"],
    active: ["element-monitoring-controller-active"],
  },
};

interface ControlRoomDiagramProps {
  onItemSelect: (item: ControlRoomLabelItem | null) => void;
}

export const ControlRoomDiagram: React.FC<ControlRoomDiagramProps> = ({ onItemSelect }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  // Track when SVG content is ready
  const [svgReady, setSvgReady] = useState<boolean>(false);

  // Track which items are currently hovered (showing -active version on hover)
  const [hoveredItems, setHoveredItems] = useState<Set<ControlRoomLabelItem>>(new Set());

  useEffect(() => {
    setMounted(true);
  }, []);

  // Effect to detect when SVG content is available
  useEffect(() => {
    if (!mounted || !divRef.current) {
      setSvgReady(false);
      return;
    }

    // Use a timeout to ensure SVG is fully rendered
    const timeoutId = setTimeout(() => {
      setSvgReady(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [mounted]);

  // memoize item handles - recalculate when SVG is ready
  const itemHandles = useMemo<Partial<Record<ControlRoomLabelItem, Element>>>(() => {
    if (!svgReady || !divRef.current) {
      return {};
    }

    const handles = {
      "label-interface": divRef.current.querySelector(".label-interface") ?? undefined,
      "label-speakers": divRef.current.querySelector(".label-speakers") ?? undefined,
      "label-headphones": divRef.current.querySelector(".label-headphones") ?? undefined,
      "label-monitoring-controller": divRef.current.querySelector(".label-monitoring-controller") ?? undefined,
      "label-studio-room": divRef.current.querySelector(".label-studio-room") ?? undefined,
    };

    return handles;
  }, [svgReady]);

  // memoize interactive element handles
  const interactiveHandles = useMemo<Partial<Record<ControlRoomInteractiveItem, Element>>>(() => {
    if (!svgReady || !divRef.current) {
      return {};
    }

    const handles: Partial<Record<ControlRoomInteractiveItem, Element>> = {};

    // Get all interactive elements
    Object.values(labelToInteractiveMap).forEach(({ normal, active }) => {
      // Handle arrays of elements
      normal.forEach((elementClass) => {
        handles[elementClass] = divRef.current!.querySelector(`.${elementClass}`) ?? undefined;
      });
      active.forEach((elementClass) => {
        handles[elementClass] = divRef.current!.querySelector(`.${elementClass}`) ?? undefined;
      });
    });

    return handles;
  }, [svgReady]);

  // Effect to hide/show active elements based on hoveredItems state
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    // When label is hovered: hide normal elements, show -active elements
    // When label is not hovered: show normal elements, hide -active elements
    Object.entries(labelToInteractiveMap).forEach(([label, { normal, active }]) => {
      const isHovered = hoveredItems.has(label as ControlRoomLabelItem);

      // Normal elements: visible when NOT hovered, hidden when hovered
      normal.forEach((elementClass) => {
        const normalElement = interactiveHandles[elementClass] as SVGElement;
        if (normalElement) {
          normalElement.style.display = isHovered ? "none" : "";
        }
      });

      // Active elements: hidden when NOT hovered, visible when hovered
      active.forEach((elementClass) => {
        const activeElement = interactiveHandles[elementClass] as SVGElement;
        if (activeElement) {
          activeElement.style.display = isHovered ? "" : "none";
        }
      });
    });
  }, [svgReady, hoveredItems, interactiveHandles]);

  // Effect to add 'selected' class to hovered labels
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    // Toggle 'selected' class on labels based on hover state
    Object.entries(itemHandles).forEach(([label, handle]) => {
      const labelItem = label as ControlRoomLabelItem;
      const isHovered = hoveredItems.has(labelItem);

      if (handle) {
        if (isHovered) {
          handle.classList.add("selected");
        } else {
          handle.classList.remove("selected");
        }
      }
    });
  }, [svgReady, hoveredItems, itemHandles]);

  // setup hover and click listeners
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    const clickListeners: Record<string, (event: Event) => void> = {};
    const mouseEnterListeners: Record<string, (event: Event) => void> = {};
    const mouseLeaveListeners: Record<string, (event: Event) => void> = {};

    // Add hover and click listeners to individual labels
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (!handle) {
        // eslint-disable-next-line no-console
        return;
      }

      const labelItem = id as ControlRoomLabelItem;

      // Click listener - triggers onItemSelect
      clickListeners[id] = (event: Event) => {
        // Prevent event from bubbling to the container
        event.stopPropagation();
        onItemSelect(labelItem);
      };

      // Mouse enter listener - shows active state
      mouseEnterListeners[id] = () => {
        setHoveredItems(new Set([labelItem]));
      };

      // Mouse leave listener - hides active state
      mouseLeaveListeners[id] = () => {
        setHoveredItems(new Set());
      };

      handle.addEventListener("click", clickListeners[id]);
      handle.addEventListener("mouseenter", mouseEnterListeners[id]);
      handle.addEventListener("mouseleave", mouseLeaveListeners[id]);
    });

    // Add click listener to the container for click-outside detection
    const containerClickListener = (event: Event): void => {
      // Check if the click target is one of the label elements
      const target = event.target as Element;
      const isLabelClick = Object.values(itemHandles).some(
        (handle) => handle && (handle === target || handle.contains(target))
      );

      if (!isLabelClick) {
        onItemSelect(null);
      }
    };

    const containerElement = divRef.current;
    containerElement.addEventListener("click", containerClickListener);

    return () => {
      // Remove all listeners
      Object.entries(itemHandles).forEach(([id, handle]) => {
        if (handle) {
          if (clickListeners[id]) {
            handle.removeEventListener("click", clickListeners[id]);
          }
          if (mouseEnterListeners[id]) {
            handle.removeEventListener("mouseenter", mouseEnterListeners[id]);
          }
          if (mouseLeaveListeners[id]) {
            handle.removeEventListener("mouseleave", mouseLeaveListeners[id]);
          }
        }
      });

      // Remove container listener
      if (containerElement) {
        containerElement.removeEventListener("click", containerClickListener);
      }
    };
  }, [svgReady, itemHandles, onItemSelect]);

  return (
    <div ref={divRef}>
      <ControlRoomSvg />
      {/* Inline styles to hide -active elements by default */}
      <style>
        {`
        .label-interface-active,
        .label-speakers-active,
        .label-headphones-active,
        .label-monitoring-controller-active,
        .label-studio-room-active {
          display: none;
        }

        .selected rect {
          stroke: #6E32F0 !important;
          stroke-width: 2 !important;
        }
      `}
      </style>
    </div>
  );
};
